import { useState, useEffect, useRef } from 'react';

const useContactLogic = () => {
  const [screenSize, setScreenSize] = useState('desktop');
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    empresa: '',
    whatsapp: '',
    colaboradores: '',
    servicos: [],
    valorInvestimento: '',
    prazo: '',
  });
  const [captchaToken, setCaptchaToken] = useState('');
  const [emailError, setEmailError] = useState('');
  const [whatsappError, setWhatsappError] = useState('');
  const [formStatus, setFormStatus] = useState('default'); // default | sending | saved | emailsent | error

  const formRef = useRef(null);

  // Detecta tela e define se é mobile
  useEffect(() => {
    const updateScreenSize = () => {
      setScreenSize(window.innerWidth <= 768 ? 'mobile' : 'desktop');
    };
    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Ao entrar no formulário (mobile), faz scroll até ele e bloqueia o resto da página
  useEffect(() => {
    const shouldScrollToFormStatus =
      screenSize === 'mobile' && formStatus !== 'default' && formStatus !== 'idle';
  
    if (shouldScrollToFormStatus && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  
    return () => {
      document.body.style.overflow = '';
    };
  }, [screenSize, formStatus]);
  

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validateWhatsapp = (whatsapp) => /^\(\d{2}\) \d{5}-\d{4}$/.test(whatsapp);

  const formatWhatsapp = (value) => {
    value = value.replace(/\D/g, '');
    if (value.length > 11) value = value.slice(0, 11);
    if (value.length > 10)
      return `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7, 11)}`;
    if (value.length > 6)
      return `(${value.slice(0, 2)}) ${value.slice(2, 6)}-${value.slice(6)}`;
    if (value.length > 2)
      return `(${value.slice(0, 2)}) ${value.slice(2)}`;
    if (value.length > 0)
      return `(${value}`;
    return value;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'email') {
      setEmailError(validateEmail(value) ? '' : 'E-mail inválido');
    }

    if (name === 'whatsapp') {
      const formattedValue = formatWhatsapp(value);
      setFormData((prev) => ({ ...prev, whatsapp: formattedValue }));
      setWhatsappError(
        validateWhatsapp(formattedValue) ? '' : 'Número inválido (ex: (11) 98765-4321)'
      );
      return;
    }

    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        servicos: checked
          ? [...prev.servicos, value]
          : prev.servicos.filter((s) => s !== value),
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      setFormStatus("error");
      return;
    }

    setFormStatus("sending");

    try {
      const response = await fetch(`${process.env.REACT_APP_CONTACT_API_URL}/submit-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: process.env.REACT_APP_FORM_SECRET_TOKEN
        },
        body: JSON.stringify({ formData, captchaToken }),
      });

      const data = await response.json();

      if (response.ok) {
        setFormStatus("saved"); // etapa 2: salvo

        // etapa 3: e-mails enviados
        setTimeout(() => {
          setFormStatus("emailsent");
        }, 600);
      } else {
        console.error("❌ Erro no envio:", data);
        setFormStatus("error");
      }
    } catch (error) {
      console.error("❌ Erro ao conectar com o servidor:", error);
      setFormStatus("error");
    }
  };

  const isFormValid = () =>
    formData.nome.trim() !== '' &&
    validateEmail(formData.email) &&
    validateWhatsapp(formData.whatsapp) &&
    formData.empresa.trim() !== '' &&
    formData.colaboradores !== '' &&
    formData.servicos.length > 0 &&
    formData.valorInvestimento !== '' &&
    formData.prazo !== '' &&
    captchaToken !== '';

  return {
    screenSize,
    formData,
    setFormData,
    captchaToken,
    setCaptchaToken,
    emailError,
    whatsappError,
    handleChange,
    handleSubmit,
    isFormValid,
    formStatus,
    setFormStatus,
    formRef, // <- usado para scroll
  };
};

export default useContactLogic;
